import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { getStorefrontConfig } from "lib/storefront";
import { post } from "lib/api";
import { getAppleIdToken, getGoogleAccessToken, getMicrosoftIdToken } from "lib/socialAuth";

const resolveRedirectUriForCurrentHost = (configured, fallbackPath) => {
  const fallback = `${window.location.origin}${fallbackPath}`;
  const value = String(configured || "").trim();
  if (!value) return fallback;
  try {
    const parsed = new URL(value);
    if (parsed.host !== window.location.host) {
      return `${window.location.origin}${parsed.pathname || fallbackPath}`;
    }
    return parsed.toString();
  } catch {
    return fallback;
  }
};

function getErrorMessage(err) {
  const data = err?.response?.data;
  if (typeof data?.error === "string") return data.error;
  if (typeof data?.error?.message === "string") return data.error.message;
  if (typeof data?.message === "string") return data.message;
  if (typeof err?.message === "string") return err.message;
  return "Registration failed";
}

function safeNextPath(value) {
  const next = String(value || "").trim();
  if (!next.startsWith("/")) return "";
  if (next.includes("/authentication/") || next.endsWith("/sign-out")) return "";
  return next;
}

/**
 * End-customer storefront registration (portal tenants).
 * Redirects to /account after success, not staff post-login.
 */
export default function useStorefrontRegister() {
  const storefront = getStorefrontConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, loginWithSocial } = useAuth();

  const isMobile =
    location.pathname === "/m/register" || location.pathname.startsWith("/m/register");
  const queryNext = safeNextPath(searchParams.get("next"));
  const defaultRedirect = isMobile ? "/m/account" : "/account";
  const redirectAfterRegister = queryNext || defaultRedirect;
  const signInPath = isMobile ? "/m/authentication/sign-in" : "/authentication/sign-in";

  const googleClientId = String(
    process.env.REACT_APP_GOOGLE_CLIENT_ID ||
      process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID ||
      "",
  ).trim();
  const appleClientId = String(process.env.REACT_APP_APPLE_CLIENT_ID || "").trim();
  const microsoftClientId = String(
    process.env.REACT_APP_MICROSOFT_CLIENT_ID || "",
  ).trim();
  const microsoftTenantId =
    String(process.env.REACT_APP_MICROSOFT_TENANT_ID || "common").trim() ||
    "common";
  const appleRedirectUri = resolveRedirectUriForCurrentHost(
    process.env.REACT_APP_APPLE_REDIRECT_URI,
    "/authentication/sign-in",
  );
  const microsoftRedirectUri = resolveRedirectUriForCurrentHost(
    process.env.REACT_APP_MICROSOFT_REDIRECT_URI,
    "/msal-popup.html",
  );

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [phone, setPhone] = useState("");
  const [addressLine, setAddressLine] = useState("");
  const [addressLookupLoading, setAddressLookupLoading] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState("");
  const [addressCandidates, setAddressCandidates] = useState([]);
  const [selectedAddressPlaceId, setSelectedAddressPlaceId] = useState("");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const addressLookupSeqRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const selectedAddressCandidate = useMemo(() => {
    if (
      selectedAddress &&
      String(selectedAddress?.placeId || "") === String(selectedAddressPlaceId || "")
    ) {
      return selectedAddress;
    }
    return (
      addressCandidates.find(
        (row) => String(row?.placeId || "") === String(selectedAddressPlaceId || ""),
      ) || null
    );
  }, [addressCandidates, selectedAddress, selectedAddressPlaceId]);

  const registrationAddressLookup = useMemo(
    () => ({
      candidatesByKey: { registrationAddress: addressCandidates },
      draftsByKey: { registrationAddress: addressLine },
      loadingByKey: { registrationAddress: addressLookupLoading },
      errorsByKey: { registrationAddress: addressLookupError },
      setDraftForRow: (_rowKey, value) => setAddressLine(String(value || "")),
      applyCandidateForRow: () => {},
    }),
    [addressCandidates, addressLine, addressLookupLoading, addressLookupError],
  );

  const canSubmit = useMemo(() => {
    if (!name.trim()) return false;
    if (!email.trim()) return false;
    if (!password) return false;
    if (password !== confirm) return false;
    if (!phone.trim()) return false;
    if (!addressLine.trim()) return false;
    if (!selectedAddressPlaceId.trim()) return false;
    return true;
  }, [name, email, password, confirm, phone, addressLine, selectedAddressPlaceId]);

  const selectAddressCandidate = (candidate) => {
    const formatted = String(
      candidate?.formattedAddress || candidate?.addressLine || "",
    ).trim();
    const placeId = String(candidate?.placeId || "").trim();
    if (!formatted || !placeId) return;
    addressLookupSeqRef.current += 1;
    setSelectedAddress(candidate);
    setAddressCandidates((prev) => [
      candidate,
      ...prev.filter((row) => String(row?.placeId || "") !== placeId),
    ]);
    setAddressLine(formatted);
    setSelectedAddressPlaceId(placeId);
    setAddressLookupError("");
  };

  useEffect(() => {
    const value = String(addressLine || "").trim();
    setAddressLookupError("");
    const selectedFormatted = String(
      selectedAddress?.formattedAddress || selectedAddress?.addressLine || "",
    ).trim();
    if (selectedAddressPlaceId && selectedFormatted && selectedFormatted === value) {
      setAddressLookupLoading(false);
      return undefined;
    }
    if (!value || value.length < 6) {
      setAddressCandidates([]);
      setAddressLookupLoading(false);
      return undefined;
    }

    const requestSeq = addressLookupSeqRef.current + 1;
    addressLookupSeqRef.current = requestSeq;
    const timer = setTimeout(async () => {
      try {
        setAddressLookupLoading(true);
        const response = await post("/api/storefront/address/lookup", {
          addressLine: value,
        });
        if (addressLookupSeqRef.current !== requestSeq) return;
        const candidates = Array.isArray(response?.candidates) ? response.candidates : [];
        setAddressCandidates(candidates);
        const stillSelected =
          selectedAddressPlaceId && selectedFormatted && selectedFormatted === value;
        if (
          !stillSelected &&
          !candidates.some(
            (row) => String(row?.placeId || "") === String(selectedAddressPlaceId || ""),
          )
        ) {
          setSelectedAddressPlaceId("");
          setSelectedAddress(null);
        }
      } catch (err) {
        if (addressLookupSeqRef.current !== requestSeq) return;
        setAddressCandidates([]);
        setSelectedAddressPlaceId("");
        setSelectedAddress(null);
        setAddressLookupError(getErrorMessage(err));
      } finally {
        if (addressLookupSeqRef.current === requestSeq) {
          setAddressLookupLoading(false);
        }
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [addressLine, selectedAddress, selectedAddressPlaceId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    if (!canSubmit) return;
    try {
      setLoading(true);
      await post("/api/storefront/auth/register", {
        email: String(email || "").trim().toLowerCase(),
        password,
        companyKey: storefront?.companyKey || undefined,
        name: String(name || "").trim(),
        customerKind: "end_user",
        phone: String(phone || "").trim(),
        addressLine: String(addressLine || "").trim(),
        addressPlaceId: String(selectedAddressPlaceId || "").trim(),
      });

      try {
        const ok = await login(String(email || "").trim().toLowerCase(), password);
        if (ok) {
          navigate(redirectAfterRegister, { replace: true });
        } else {
          setMessage({ type: "success", text: "Account created. Please sign in." });
          navigate(signInPath, { replace: true });
        }
      } catch {
        setMessage({ type: "success", text: "Account created. Please sign in." });
        navigate(signInPath, { replace: true });
      }
    } catch (err) {
      setMessage({ type: "error", text: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  const handleSocialRegister = async (provider) => {
    const normalized = String(provider || "").trim().toLowerCase();
    if (!normalized) return;
    const providerEnabled =
      (normalized === "google" && !!googleClientId) ||
      (normalized === "apple" && !!appleClientId) ||
      (normalized === "microsoft" && !!microsoftClientId);
    if (!providerEnabled) {
      setMessage({
        type: "error",
        text: `${normalized} sign in is not configured yet.`,
      });
      return;
    }
    if (!name.trim()) {
      setMessage({ type: "error", text: "Name is required." });
      return;
    }
    if (!phone.trim()) {
      setMessage({ type: "error", text: "Phone is required." });
      return;
    }
    if (!selectedAddressPlaceId.trim()) {
      setMessage({
        type: "error",
        text: "Select a verified address to continue.",
      });
      return;
    }
    try {
      setLoading(true);
      setMessage(null);
      let payload = {};
      if (normalized === "google") {
        payload = await getGoogleAccessToken({ clientId: googleClientId });
      } else if (normalized === "apple") {
        payload = await getAppleIdToken({
          clientId: appleClientId,
          redirectURI: appleRedirectUri,
        });
      } else if (normalized === "microsoft") {
        payload = await getMicrosoftIdToken({
          clientId: microsoftClientId,
          tenantId: microsoftTenantId,
          redirectUri: microsoftRedirectUri,
        });
      }
      const ok = await loginWithSocial({
        provider: normalized,
        ...payload,
        companyKey: storefront?.companyKey || undefined,
        register: true,
        name: String(name || "").trim(),
        phone: String(phone || "").trim(),
        addressLine: String(addressLine || "").trim(),
        addressPlaceId: String(selectedAddressPlaceId || "").trim(),
      });
      if (ok) navigate(redirectAfterRegister, { replace: true });
      else setMessage({ type: "error", text: "Social registration failed." });
    } catch (err) {
      setMessage({ type: "error", text: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  return {
    name,
    setName,
    email,
    setEmail,
    password,
    setPassword,
    confirm,
    setConfirm,
    phone,
    setPhone,
    addressLine,
    selectedAddressCandidate,
    selectedAddressPlaceId,
    registrationAddressLookup,
    selectAddressCandidate,
    onAddressTyping: () => {
      setSelectedAddressPlaceId("");
      setSelectedAddress(null);
      setAddressLookupError("");
    },
    loading,
    message,
    canSubmit,
    handleSubmit,
    handleSocialRegister,
    googleClientId,
    appleClientId,
    microsoftClientId,
    signInPath,
    isMobile,
  };
}
